import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { canSeeInsights } from '../lib/permissions';
import {
  IconArrowUpRight,
  IconChevronDown,
  IconRefresh,
} from '@tabler/icons-react';
import {
  KPI_REQUEST_SELECT,
  committedSpend,
  invoicedSpend,
  firstPassMatchRate,
  openDiscrepancyExposure,
  offCatalogRate,
  receivedNotInvoiced,
  requisitionToOrderDays,
  shortClosedRate,
  deliveredByNeedBy,
  lowOrOutOfStock,
  BENCHMARKS,
  EMPTY,
} from '../lib/purchasingKpis';

// #308 executive dashboard, Phase 1. Session 74z6.
//
// Approved at the mockup gate: docs/gates/Stede_ExecutiveDashboard_Gate_308.html
//
// PHASE 1 IS DELIBERATELY THE SUMMARY LAYER ONLY. The gate's three-layer shape —
// summary, trend, breakdown — lands in full at the Sep 5-6 sprint. What ships
// here is the layer an executive reads first, plus the drill contract, because a
// half-built trend chart teaches the reader the page is unfinished.
//
// THE HARD REQUIREMENT IS THE EMPTY STATE, not the populated one. Dallas starts
// Sep 1 with no purchasing history and a KPI surface's worst moment is its first.
// Every figure here comes from purchasingKpis.js, which returns a reason rather
// than a zero, and this page's job is to render that reason as a sentence.

const PURCHASING = '#EF9F27';
const GOOD = '#14805C';
const BAD = '#C43C3B';

function money(n) {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function pct(n) {
  if (n == null) return '—';
  return Math.round(n) + '%';
}

// The empty-state sentence for each reason. Written here rather than in the
// calculation layer because it is copy, not math — and generated FROM the reason
// rather than guessed at by the tile, so a new reason cannot render blank.
const EMPTY_COPY = {
  [EMPTY.NO_ORDERS]:       { text: 'No orders yet',        sub: 'fills when the first order is placed' },
  [EMPTY.NO_OPEN]:         { text: 'No open orders',       sub: 'everything ordered has been billed' },
  [EMPTY.NO_INVOICES]:     { text: 'No invoices',          sub: 'in this period' },
  [EMPTY.NO_RECEIPTS]:     { text: '—',                    sub: 'needs 1 received line' },
  [EMPTY.NO_LINES]:        { text: '—',                    sub: 'needs 1 ordered line' },
  [EMPTY.NO_NEED_BY]:      { text: '—',                    sub: 'not enough need-by dates yet' },
  [EMPTY.NO_STOCK]:        { text: '—',                    sub: 'no stock records' },
  [EMPTY.NOTHING_FLAGGED]: { text: 'Nothing flagged',      sub: 'all clear' },
  [EMPTY.ALL_INVOICED]:    { text: 'All receipts invoiced', sub: 'nothing outstanding' },
};

function emptyCopy(kpi) {
  const base = EMPTY_COPY[kpi?.empty] || { text: '—', sub: 'no data yet' };
  // "needs 3 more" beats "not enough" — the tile says how far away it is.
  if (kpi?.needs > 1 && kpi.empty === EMPTY.NO_NEED_BY) {
    return { ...base, sub: `needs ${kpi.needs} more need-by dates` };
  }
  return base;
}

// ── Tiles ───────────────────────────────────────────────────────────────────
// Defined at module level, never inside the page component — a component
// function declared in a parent's body remounts on every render.

// `label` is JSX so a two-line tile can break where it should. `labelText` is
// the same words as a plain string, because interpolating the element into an
// aria-label renders the literal "[object Object]" to a screen reader — which is
// exactly what it did until the accessibility tree was read on the live page.
function Tile({ label, labelText, kpi, format, accent, onDrill, drillLabel }) {
  const isEmpty = !kpi || kpi.value == null;
  const copy = isEmpty ? emptyCopy(kpi) : null;
  const clickable = !!onDrill;

  return (
    <div
      onClick={onDrill}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrill(); } }) : undefined}
      aria-label={clickable ? `${labelText} — ${drillLabel}` : undefined}
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E8E8',
        borderTop: `3px solid ${isEmpty ? '#CFC5B4' : (accent || PURCHASING)}`,
        borderRadius: '0 0 7px 7px',
        padding: '12px 13px 11px',
        position: 'relative',
        cursor: clickable ? 'pointer' : 'default',
        minWidth: 0,
      }}
    >
      {clickable && (
        <IconArrowUpRight
          size={15}
          color={isEmpty ? '#B8B8B8' : '#A8690A'}
          style={{ position: 'absolute', top: 9, right: 9 }}
        />
      )}
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase',
        color: '#7A7A7A', lineHeight: 1.25, paddingRight: 18, minHeight: 26,
      }}>{label}</div>

      <div style={{
        fontSize: isEmpty ? 16 : 27,
        fontWeight: 700,
        lineHeight: 1.1,
        marginTop: 3,
        color: isEmpty ? '#8A8A8A' : '#0F0F0F',
        fontVariantNumeric: 'tabular-nums',
      }}>{isEmpty ? copy.text : format(kpi.value)}</div>

      <div style={{ fontSize: 11.5, color: '#9A9A9A', marginTop: 2 }}>
        {isEmpty ? copy.sub : (kpi.subtitle || '')}
      </div>
    </div>
  );
}

function ControlRow({ label, kpi, format, benchmark }) {
  const isEmpty = !kpi || kpi.value == null;
  const copy = isEmpty ? emptyCopy(kpi) : null;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'baseline',
      padding: '8px 0', borderBottom: '1px solid #F0F0F0',
    }}>
      <div>
        <div style={{ fontSize: 13.5, color: '#0F0F0F', fontWeight: 500 }}>{label}</div>
        {/* The benchmark is the SUBTITLE, never the value — one is measured, the
            other is quoted, and they must never be confusable. */}
        <div style={{ fontSize: 11, color: '#9A9A9A' }}>
          {isEmpty ? copy.sub : (benchmark || kpi.subtitle || '')}
        </div>
      </div>
      <div style={{
        fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        color: isEmpty ? '#B8B8B8' : '#0F0F0F',
      }}>{isEmpty ? '—' : format(kpi.value)}</div>
    </div>
  );
}

// ── Period ──────────────────────────────────────────────────────────────────

function monthRange(offset = 0) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    from: iso(start),
    to: iso(end),
    label: start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    // Carried rather than derived by splitting `label` on a space — that gave
    // the tile "INVOICED ALL" for the all-time period.
    short: start.toLocaleDateString('en-US', { month: 'long' }),
  };
}

const PERIODS = [
  { id: 'this_month', build: () => monthRange(0) },
  { id: 'last_month', build: () => monthRange(-1) },
  { id: 'all_time',   build: () => ({ from: null, to: null, label: 'All time', short: 'all time' }) },
];

// ── Page ────────────────────────────────────────────────────────────────────

export default function InsightsPage({ setActive }) {
  const auth = useAuth();
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = auth;

  const [periodId, setPeriodId] = useState('this_month');
  const [requests, setRequests] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const period = (PERIODS.find(p => p.id === periodId) || PERIODS[0]).build();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reqRes, stockRes] = await Promise.all([
        supabase
          .from('material_requests')
          .select(KPI_REQUEST_SELECT)
          .in('status', ['ordered', 'received', 'closed']),
        // #803 -- each row's item switch rides along, so the tile counts only
        // switched-on items, as Stock Home lists them.
        supabase
          .from('inventory_stock')
          .select('item_id, branch_id, qty_on_hand, reorder_point, inventory_items ( is_active )'),
      ]);
      if (reqRes.error) throw reqRes.error;
      if (stockRes.error) throw stockRes.error;
      setRequests(reqRes.data || []);
      setStock(stockRes.data || []);
    } catch (e) {
      // An empty dashboard and a broken one look identical, so a failure says so
      // rather than falling through to the day-one empty state.
      setError(e.message || 'Could not load insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!canSeeInsights({ isAdmin, isManager, isExecutive, isPurchasing })) {
    return (
      <div className="page-header">
        <div className="page-title">Insights</div>
        <div className="page-sub">You do not have access to this page.</div>
      </div>
    );
  }

  const opts = { from: period.from, to: period.to };

  // Purchasing KPIs take no branch argument by design — purchasing is run from
  // Houston for requests originating at Dallas. Inventory does take one, and in
  // Phase 1 nothing passes it. See #311.
  const committed   = committedSpend(requests, opts);
  const invoiced    = invoicedSpend(requests, opts);
  const firstPass   = firstPassMatchRate(requests, opts);
  const discrepancy = openDiscrepancyExposure(requests, opts);
  const offCatalog  = offCatalogRate(requests, opts);
  const gr          = receivedNotInvoiced(requests, opts);
  const reqToOrder  = requisitionToOrderDays(requests, opts);
  const shortClose  = shortClosedRate(requests, opts);
  const needBy      = deliveredByNeedBy(requests, opts);
  const lowStock    = lowOrOutOfStock(stock, {});

  // Subtitles are attached here rather than inside the calculation layer, which
  // returns numbers and never copy.
  // "as of today" is not decoration. Committed spend is a BALANCE, not a flow:
  // it is what is open right now, so it deliberately ignores the period selector
  // while every money figure beside it honours it. Caught on the live page with
  // the period set to July — the tile sat there reading $117 under a July
  // heading, which is correct and reads as a bug unless the tile says why.
  if (committed.value != null)   committed.subtitle   = `on ${committed.orderCount} open order${committed.orderCount === 1 ? '' : 's'} · as of today`;
  if (invoiced.value != null)    invoiced.subtitle    = `${invoiced.invoiceCount} invoice${invoiced.invoiceCount === 1 ? '' : 's'}${invoiced.creditCount ? `, ${invoiced.creditCount} credit${invoiced.creditCount === 1 ? '' : 's'}` : ''}`;
  if (firstPass.value != null)   firstPass.subtitle   = `${firstPass.matched} of ${firstPass.counted} clean`;
  if (discrepancy.value != null) discrepancy.subtitle = `${discrepancy.invoiceCount} invoice${discrepancy.invoiceCount === 1 ? '' : 's'} flagged`;
  if (gr.value != null)          gr.subtitle          = `${gr.orderCount} order${gr.orderCount === 1 ? '' : 's'} awaiting invoice`;
  // #511 -- the count now includes empty shelves with no reorder point, so the
  // subtitle says how many of them are out rather than restating zero.
  if (lowStock.value != null)    lowStock.subtitle    = `${lowStock.out} of them out`;
  if (shortClose.value != null)  shortClose.subtitle  = shortClose.topReason ? `top reason: ${shortClose.topReason}` : '';
  if (needBy.value != null)      needBy.subtitle      = `${needBy.onTime} of ${needBy.total} lines`;

  const nothingYet = !loading && !error && requests.length === 0;

  return (
    <div>
      <div className="page-header" style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, flexWrap: 'wrap', marginBottom: '1rem',
      }}>
        <div>
          <div className="page-title">Insights</div>
          <div className="page-sub">
            Purchasing &amp; inventory · all branches · {period.label}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <select
              value={periodId}
              onChange={e => setPeriodId(e.target.value)}
              aria-label="Reporting period"
              style={{
                appearance: 'none', fontSize: 16, fontWeight: 600, padding: '6px 26px 6px 11px',
                borderRadius: 6, border: '1px solid #D8D8D8', background: '#FFFFFF',
                color: '#0F0F0F', cursor: 'pointer',
              }}
            >
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="all_time">All time</option>
            </select>
            <IconChevronDown size={14} color="#7A7A7A" style={{ position: 'absolute', right: 8, top: 10, pointerEvents: 'none' }} />
          </div>
          <button
            onClick={load}
            aria-label="Refresh"
            style={{
              display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600,
              padding: '7px 11px', borderRadius: 6, border: '1px solid #D8D8D8',
              background: '#FFFFFF', color: '#4A4A4A', cursor: 'pointer',
            }}
          >
            <IconRefresh size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          border: '1px solid #E8B4B3', background: '#FBEAE9', borderRadius: 7,
          padding: '12px 14px', marginBottom: 14, color: '#8E2A29', fontSize: 14,
        }}>
          <strong>Insights could not load.</strong> {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#9A9A9A', fontSize: 14 }}>
          Loading…
        </div>
      )}

      {!loading && !error && (
        <>
          {nothingYet && (
            <div style={{
              borderLeft: `3px solid ${PURCHASING}`, background: '#FDF3E2',
              borderRadius: '0 6px 6px 0', padding: '12px 16px', marginBottom: 14,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                color: '#A8690A', marginBottom: 3,
              }}>Day one</div>
              <div style={{ fontSize: 14, color: '#3A3A3A', lineHeight: 1.5 }}>
                No purchasing history yet. These figures start filling as orders are placed
                and invoices are entered — <strong>nothing here is broken.</strong>
              </div>
            </div>
          )}

          {/* Summary layer */}
          <div style={{
            display: 'grid', gap: 11, marginBottom: 11,
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          }}>
            <Tile
              label={<>Committed<br />spend</>}
              labelText="Committed spend"
              kpi={committed}
              format={money}
              onDrill={() => setActive('purchasing_queue')}
              drillLabel="open the purchasing queue"
            />
            <Tile
              label={<>Invoiced<br />{period.short}</>}
              labelText={`Invoiced ${period.short}`}
              kpi={invoiced}
              format={money}
              onDrill={() => setActive('purchasing_invoicing')}
              drillLabel="open invoicing"
            />
            <Tile
              label={<>First-pass<br />match rate</>}
              labelText="First-pass match rate"
              kpi={firstPass}
              format={pct}
              accent={GOOD}
              onDrill={() => setActive('purchasing_invoicing')}
              drillLabel="open invoicing discrepancies"
            />
            <Tile
              label={<>Open discrepancy<br />exposure</>}
              labelText="Open discrepancy exposure"
              kpi={discrepancy}
              format={money}
              accent={BAD}
              onDrill={() => setActive('purchasing_invoicing')}
              drillLabel="open invoicing discrepancies"
            />
          </div>

          <div style={{
            display: 'grid', gap: 11,
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          }}>
            {/* Control */}
            <div style={{
              background: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: 7,
              padding: '12px 14px 8px',
            }}>
              <div style={{
                fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                color: '#0F0F0F', paddingBottom: 8, marginBottom: 4,
                borderBottom: '1px solid #EDEDED',
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              }}>
                <span>Control</span>
                <span style={{ fontSize: 10.5, fontWeight: 500, letterSpacing: 0, textTransform: 'none', color: '#A8690A' }}>
                  benchmarked
                </span>
              </div>
              <ControlRow
                label="Off-catalog spend"
                kpi={offCatalog}
                format={v => v.toFixed(1) + '%'}
                benchmark={BENCHMARKS.offCatalogRate.label}
              />
              <ControlRow
                label="Requisition → order"
                kpi={reqToOrder}
                format={v => v.toFixed(1) + ' d'}
                benchmark={BENCHMARKS.requisitionToOrderDays.label}
              />
              <ControlRow
                label="Short-closed lines"
                kpi={shortClose}
                format={v => v.toFixed(1) + '%'}
              />
              {/* Deliberately NOT called on-time delivery — Stede holds the
                  requester's need-by date, never a vendor promise. */}
              <ControlRow
                label="Delivered by need-by date"
                kpi={needBy}
                format={pct}
              />
            </div>

            {/* Exposure & stock */}
            <div style={{
              background: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: 7,
              padding: '12px 14px 12px',
            }}>
              <div style={{
                fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                color: '#0F0F0F', paddingBottom: 8, marginBottom: 8,
                borderBottom: '1px solid #EDEDED',
              }}>Exposure &amp; stock</div>

              <div style={{
                display: 'grid', gap: 10,
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              }}>
                <Tile
                  label={<>Received,<br />not invoiced</>}
                  labelText="Received, not invoiced"
                  kpi={gr}
                  format={money}
                  onDrill={() => setActive('purchasing_invoicing')}
                  drillLabel="open awaiting invoice"
                />
                <Tile
                  label={<>Low or<br />out of stock</>}
                  labelText="Low or out of stock"
                  kpi={lowStock}
                  format={v => `${v} item${v === 1 ? '' : 's'}`}
                  accent={GOOD}
                  onDrill={() => setActive('inv_home')}
                  drillLabel="open stock home"
                />
              </div>

              <div
                onClick={() => setActive('inv_offcatalog')}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive('inv_offcatalog'); } }}
                style={{
                  marginTop: 10, fontSize: 12.5, color: '#A8690A', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                Review off-catalog requests <IconArrowUpRight size={13} />
              </div>
            </div>
          </div>

          <div style={{ fontSize: 11.5, color: '#A8A8A8', marginTop: 14, lineHeight: 1.6 }}>
            Phase 1 · summary layer. Trend and breakdown panels, export, and the aging
            drill land at the Sep 5–6 sprint. Purchasing figures are company-wide by
            design — buying is run from Houston for every branch.
          </div>
        </>
      )}
    </div>
  );
}
